import React, { useEffect, useRef, useState } from 'react';

import
{
  Button,
  Input,
  Text,
  Radio,
  Icon,
} from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';


import { PlatformType } from '../../types/project';
import { checkProjectDomain } from '../../services/projectDomain';
import type { ProjectStacksState } from '../../hooks/useProjectStacks';
import {
  DrawerWidth,
  StackPicks,
  canCreateStack,
  closesOnBackdrop,
  createStackFields,
  drawerWidth,
  listedChoice,
} from '../../utils/stackPicker';
import { slugify } from '../../utils/slug';
import { sanitizeChange } from '../../utils/sanitizeInput';
import { ProjectLlmSelect } from './ProjectLlmSelect';
import { StackNameInFooter, TechStackField } from './TechStackField';
import { GENERATED_APP_BASE_DOMAIN } from '../../config/domains';

const DOMAIN_CHECK_DEBOUNCE_MS = 450;

interface DomainStatus {
  kind: 'idle' | 'checking' | 'available' | 'taken' | 'error';
  msg?: string;
}

export interface UserInput {
  title?: string;
  clientName?: string;
  projectType?: number;
  platformType?: PlatformType;
  customDomain?: string;
  /** The Tech Stack chosen for a Web project: a preset's id from GET /projects/stacks,
   *  or CUSTOM_STACK for the Custom option (utils/stackPicker.ts StackChoice). */
  stack?: string;
  /** The Custom option's picks (group -> option id): {}, None in every group, the
   *  first time it is chosen, then kept while the drawer stays open, a preset
   *  chosen in between included. */
  stackPicks?: StackPicks;
  /** The Special Instructions box's text, sent with any choice. */
  customInstructions?: string;
  /** Beta-user-only. A service id, or null/undefined for the platform default. */
  llmService?: string | null;
}

interface CreateProjectModalProps {
  userInput: UserInput;
  setUserInput: (v: UserInput) => void;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
  error: string;
  /** The tech stacks GET /projects/stacks offers, as the dashboard read them when
   *  it loaded and keeps them, so the drawer knows its width as it opens, unless
   *  it opens before they are read (drawerWidth). */
  stacks: ProjectStacksState;
  /** Reads them again. */
  reloadStacks: () => void;
  /** Beta-user-only: the LLM control is rendered only when true. */
  canChooseLlm?: boolean;
}

export const CreateProjectModal: React.FC<CreateProjectModalProps> = ({
  userInput,
  setUserInput,
  isOpen,
  onClose,
  onSubmit,
  loading,
  error,
  stacks,
  reloadStacks,
  canChooseLlm = false,
}) => {
  const [isClosing, setIsClosing] = useState(false);
  const [domainStatus, setDomainStatus] = useState<DomainStatus>({ kind: 'idle' });
  // Drops responses that no longer describe the current value.
  const checkSeq = useRef(0);
  const errorRef = useRef<HTMLElement>(null);

  const custom = stacks.kind === 'loaded' ? stacks.custom : null;

  // The drawer's width (drawerWidth), decided once each time it opens and kept
  // until it closes: 720px, capped at the screen, where the list offers the
  // Custom option, else 400px. It is decided as the drawer opens from the
  // dashboard's copy of the list, read as the dashboard loads, or, for a drawer
  // opened before that copy is read, when the first list lands while it is open,
  // its Tech Stack list showing with it; until then the drawer is 400px. A list
  // read again never resizes it, so one offering the Custom option to a drawer
  // decided at 400px shows Custom's groups in one column, and while the drawer
  // slides out its width stays as it is. Once it is closed the width goes, so
  // the next opening decides again.
  const [width, setWidth] = useState<DrawerWidth | null>(null);
  const nextWidth = drawerWidth(width, stacks.kind === 'loaded' ? stacks : null, isOpen, isClosing);
  if (nextWidth !== width) setWidth(nextWidth);
  const wide = nextWidth === 720;

  // The Tech Stack list is the dashboard's copy, read again each time the drawer
  // opens and with Try again, so a stack the server stops offering leaves it.
  // Without the list a project is created on the server's default stack.
  useEffect(() => {
    if (isOpen) reloadStacks();
  }, [isOpen, reloadStacks]);

  // Read again after a create failed: the chosen stack may have been withdrawn
  // while the drawer was open, and the list then preselects the default again.
  // The failure shows at the end of the form, scrolled into view.
  useEffect(() => {
    if (!isOpen || !error) return;
    reloadStacks();
    errorRef.current?.scrollIntoView({ block: 'nearest' });
  }, [isOpen, error, reloadStacks]);

  // A Web project carries the choice the list shows: the default until the user
  // picks another, the Custom option while the list offers it, and Custom's
  // picks and the box's text only as far as the list allows them.
  useEffect(() => {
    if (!isOpen || stacks.kind !== 'loaded') return;
    const listed = listedChoice(stacks.presets, stacks.custom, userInput);
    if (listed !== userInput) setUserInput(listed);
  }, [isOpen, stacks, userInput, setUserInput]);

  const isWeb = userInput.projectType === 1;
  // The stack's name in the footer while the Custom option is chosen, where the
  // buttons keep their size and the name wraps on a narrow screen.
  const stackNameInFooter = !!custom && isWeb;

  const trimmedDomain = userInput.customDomain?.trim() || '';
  const domainCheckEnabled = isOpen && userInput.projectType === 1 && !!trimmedDomain;

  // Checked as the user types, debounced — verdict shows inside the input.
  useEffect(() => {
    checkSeq.current++;
    if (!domainCheckEnabled) {
      setDomainStatus({ kind: 'idle' });
      return;
    }
    setDomainStatus({ kind: 'checking' });
    const id = checkSeq.current;
    const timer = setTimeout(async () => {
      try {
        const result = await checkProjectDomain(trimmedDomain);
        if (id !== checkSeq.current) return;
        setDomainStatus(
          result.available
            ? { kind: 'available' }
            : { kind: 'taken', msg: result.reason || 'Domain is unavailable' }
        );
      } catch {
        if (id !== checkSeq.current) return;
        setDomainStatus({ kind: 'error', msg: "Couldn't reach the server. It'll retry as you type." });
      }
    }, DOMAIN_CHECK_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trimmedDomain, domainCheckEnabled]);

  const isValidForm = userInput.title?.trim() && (userInput.projectType === 1 || userInput.platformType);
  // While the Custom option is offered, a Web project's tech stack is one the
  // create API takes: within the cap, and with Custom, something picked or
  // written, every need met and every pick available.
  const stackApplies = !isWeb || !custom || canCreateStack(custom, userInput);
  const isSubmitDisabled = loading || !isValidForm || (!!trimmedDomain && domainStatus.kind !== 'available') || !stackApplies;

  const domainBad = domainStatus.kind === 'taken' || domainStatus.kind === 'error';
  const domainMessage =
    domainStatus.msg ||
    (domainStatus.kind === 'checking'
      ? 'Checking availability…'
      : domainStatus.kind === 'available'
      ? 'Domain is available.'
      : '');
  const domainIcon =
    domainStatus.kind === 'checking' ? (
      <Icon name={IconName.Loading} size="sm" spin className="text-text-tertiary" inheritColor />
    ) : domainStatus.kind === 'available' ? (
      <Icon name={IconName.Check} size="sm" variant="success" />
    ) : domainBad ? (
      <Icon name={IconName.Close} size="sm" variant="error" />
    ) : undefined;

  // Same rule and same caret handling as the edit modal beside it — the field
  // is the same field, and it behaving differently depending on which dialog
  // you opened is the kind of difference nobody decides on purpose.
  const handleDomainChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserInput({ ...userInput, customDomain: sanitizeChange(e, slugify) });
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(e);
  };

  if (!isOpen && !isClosing) return null;

  // A click on the backdrop leaves the drawer open while what Create Project would
  // send holds Custom's picks or special instructions (closesOnBackdrop); Cancel
  // and the close button close it.
  return (
    <div 
      className={`fixed inset-0 bg-alpha-black-70 z-50 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`} 
      onClick={closesOnBackdrop(custom, userInput.projectType, createStackFields(userInput)) ? handleClose : undefined}
    >
      <div
        className={`absolute right-0 top-0 h-full bg-bg-primary ${wide ? 'w-[720px] max-w-full' : 'w-[400px]'} flex flex-col border-l border-border-secondary-alt ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: "0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03), 0 3px 3px -1.5px rgba(10, 13, 18, 0.04)"
        }}
      >
        {/* Top Section */}
        <div className="flex p-bg-v2-3xl items-center gap-bg-v2-md self-stretch justify-between">
          <div>
            <Text variant="text-xl" weight="bold">Create New Project</Text>
          </div>
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-8 h-8 rounded-full text-fg-secondary hover:bg-bg-primary-hover hover:text-text-secondary-hover transition-all duration-200"
            disabled={loading}
          >
            <Icon name={IconName.Cross} size="lg" inheritColor />
          </button>
        </div>

        {/* Divider */}
        <div className="box-border flex flex-col gap-[16px] items-center px-[var(--spacing-3xl,24px)] py-0 w-full">
          <div className="bg-border-secondary h-px w-full" />
        </div>

        {/* The form takes the height left and its middle scrolls, so the footer
            never drops below the screen and stays at the drawer's foot. */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          {/* Middle Section */}
          <div className="flex flex-col items-center gap-bg-v2-3xl self-stretch px-bg-v2-3xl py-bg-v2-3xl overflow-y-auto flex-1 min-h-0">
            <Input
                id="project-title"
                name="project-title"
                label="Project Title"
                value={userInput.title || ''}
                onChange={e => setUserInput({ ...userInput, title: e.target.value })}
                required
                autoFocus
                placeholder="Enter project title"
                className="w-full"
              />

            {/* Divider between Project Type and Inputs, kept while the middle scrolls */}
            <div className="h-[1px] bg-border-secondary w-full shrink-0"></div>

            <div className="w-full space-y-6">
            <div className="w-full">
              <Text variant="text-md" weight="semibold" color="primary" className="mb-3">Project Type</Text>
              <div className="space-y-2">
                <Radio
                  name="projectType"
                  value={1}
                  checked={userInput.projectType === 1}
                  onChange={() => setUserInput({ ...userInput, projectType: 1 })}
                  label={<Text variant="text-sm" weight="semibold" color="tertiary" as="span">Web Application</Text>}
                  size='sm'
                />
                <Radio
                  name="projectType"
                  value={2}
                  checked={userInput.projectType === 2}
                  onChange={() => setUserInput({ ...userInput, projectType: 2 })}
                  label={<Text variant="text-sm" weight="semibold" color="tertiary" as="span">Low Code Application</Text>}
                  size='sm'
                />
              </div>
            </div>
            {userInput.projectType === 2 ?
              <div  className="animate-fade-in-up opacity-0"
              style={{
                animationDelay: `0.08s`,
                animationFillMode: 'forwards'
              }}>
               <Text variant="text-md" weight="semibold" color="primary" className="mb-3">Platform Type</Text>
                <div className="space-y-2">
                  <Radio
                    name="platformType"
                    value={1}
                    checked={userInput.platformType === PlatformType.TylerAppPlatform}
                    onChange={() => setUserInput({ ...userInput, platformType: PlatformType.TylerAppPlatform })}
                    label={<Text variant="text-sm" weight="semibold" color="tertiary" as="span">Tyler Application Platform (TAP)</Text>}
                    size='sm'
                  />
                  <Radio
                    name="platformType"
                    value={2}
                    checked={userInput.platformType === PlatformType.ServiceNow}
                    onChange={() => setUserInput({ ...userInput, platformType: PlatformType.ServiceNow })}
                    label={<Text variant="text-sm" weight="semibold" color="tertiary" as="span">ServiceNow (SN)</Text>}
                    size='sm'
                  />
                  <Radio
                    name="platformType"
                    value={3}
                    checked={userInput.platformType === PlatformType.PowerApps}
                    onChange={() => setUserInput({ ...userInput, platformType: PlatformType.PowerApps })}
                    label={<Text variant="text-sm" weight="semibold" color="tertiary" as="span">PowerApps (PA)</Text>}
                    size='sm'
                  />
                  <Radio
                    name="platformType"
                    value={4}
                    checked={userInput.platformType === PlatformType.Salesforce}
                    onChange={() => setUserInput({ ...userInput, platformType: PlatformType.Salesforce })}
                    label={<Text variant="text-sm" weight="semibold" color="tertiary" as="span">Salesforce (SF)</Text>}
                    size='sm'
                  />
                </div>
                </div>
                :
                <div  className="animate-fade-in-up opacity-0"
                  style={{
                    animationDelay: `0.08s`,
                    animationFillMode: 'forwards'
                  }}>
                  <TechStackField
                    name="techStack"
                    hint="Chosen once, when the project is created."
                    stacks={stacks}
                    reloadStacks={reloadStacks}
                    choice={userInput}
                    onChange={setUserInput}
                    boxId="create-project-instructions"
                  />
                </div>
              }
              <Input
                id="client-name"
                name="client-name"
                label="Client Name"
                value={userInput.clientName || ''}
                onChange={e => setUserInput({ ...userInput, clientName: e.target.value })}
                placeholder="Enter client name"
                className="w-full"
              />

              {userInput.projectType === 1 && (
                <div className="w-full">
                  <Text variant="text-md" weight="semibold" color="primary" className="mb-3">Custom Domain</Text>
                  <Input
                    id="custom-domain"
                    name="custom-domain"
                    value={userInput.customDomain || ''}
                    onChange={handleDomainChange}
                    autoComplete="off"
                    spellCheck={false}
                    variant={domainStatus.kind === 'available' ? 'success' : domainBad ? 'error' : 'default'}
                    endIcon={domainIcon}
                    suffix={`.${GENERATED_APP_BASE_DOMAIN}`}
                  />
                  {domainMessage && (
                    <Text
                      variant="text-xs"
                      color={domainBad ? 'error' : domainStatus.kind === 'available' ? 'success' : 'tertiary'}
                      className="mt-bg-v2-xs break-words block"
                    >
                      {domainMessage}
                    </Text>
                  )}
                </div>
              )}

              <ProjectLlmSelect
                id="project-llm-create"
                visible={canChooseLlm}
                value={userInput.llmService ?? null}
                onChange={service => setUserInput({ ...userInput, llmService: service })}
                disabled={loading}
              />
            </div>

            {error && <Text ref={errorRef} variant="text-sm" color="error" className="w-full">{error}</Text>}
          </div>

          {/* Last Section */}
          <div className="flex p-bg-v2-xl px-bg-v2-3xl justify-end items-center gap-bg-v2-xl self-stretch border-t border-border-secondary bg-bg-primary">
            {/* The stack's name while the Custom option is chosen, in view however
                far the form scrolls. */}
            {stackNameInFooter && custom && <StackNameInFooter custom={custom} choice={userInput} />}
            <Button
              variant="outline"
              label="Cancel"
              type="button"
              onClick={handleClose}
              disabled={loading}
              className={stackNameInFooter ? 'shrink-0' : undefined}
            />
            <Button
              variant="primary"
              type="submit"
              label={loading ? 'Creating…' : 'Create Project'}
              disabled={isSubmitDisabled}
              className={stackNameInFooter ? 'shrink-0' : undefined}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

